import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Form, Input, Button, notification, Modal } from "antd";
import { registerUser, sendOtp } from "../services/authAPI";
import { getSendOtpErrorDescription } from "../utils/otpErrorMessages";
import { UserAddOutlined } from "@ant-design/icons"; // Ant Design icon
import {
  formatPhoneNumber,
  handlePhoneNumberKeyDown,
} from "../utils/textUtils";

const getRegisterErrorMessage = (error) => {
  const data = error.response?.data;
  const candidates = [
    data?.detail?.message,
    data?.detail,
    data?.message,
    error.message,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      return candidate;
    }
    if (typeof candidate?.message === "string") {
      return candidate.message;
    }
  }

  return "Please try again or contact support if this keeps happening.";
};

const isOrganizationDomainRegisteredError = (error) => {
  if (error.response?.status !== 409) {
    return false;
  }

  const data = error.response?.data;
  const code = data?.detail?.code || data?.code;
  if (code === "organization_domain_registered") {
    return true;
  }

  const message = getRegisterErrorMessage(error).toLowerCase();
  return (
    message.includes("organization") &&
    message.includes("domain") &&
    message.includes("registered")
  );
};

const Register = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      await registerUser(values);
      setIsModalVisible(true);
    } catch (error) {
      console.log(error);
      const errorMessage = getRegisterErrorMessage(error);
      if (errorMessage === "Email already registered") {
        try {
          await sendOtp(values.email);
          notification.info({
            message: "Welcome back!",
            description: "We've sent a login code to your email.",
          });
          navigate("/login", { state: { email: values.email } });
        } catch (otpError) {
          notification.error({
            message: "Failed to send login code",
            description: getSendOtpErrorDescription(otpError, values.email),
          });
        }
      } else if (error.response?.status === 406) {
        setIsModalVisible(true);
      } else if (isOrganizationDomainRegisteredError(error)) {
        notification.info({
          message: "Contact your project admin",
          description: errorMessage,
          duration: 12,
        });
      } else {
        notification.error({
          message: "Registration failed",
          description: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="flex bg-white rounded-lg shadow-md w-full max-w-4xl overflow-hidden">
        {/* Image Section */}
        <div className="w-1/2 hidden md:block">
          <img
            src="/login.webp"
            alt="Register"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Form Section */}
        <div className="w-full md:w-1/2 flex flex-col justify-center p-8">
          <h1 className="text-2xl font-bold text-left text-gray-900 mb-2">
            Create Your Account
          </h1>
          <p className="text-left text-gray-600 mb-6">
            Sign up to get started with your dashboard
          </p>

          <Form form={form} onFinish={handleRegister} layout="vertical">
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input
                placeholder="Enter your work email"
                className="rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              />
            </Form.Item>

            <Form.Item
              label="First Name"
              name="first_name"
              rules={[
                { required: true, message: "Please enter your first name!" },
              ]}
            >
              <Input
                placeholder="Enter your first name"
                className="rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[
                { required: true, message: "Please enter your last name!" },
              ]}
            >
              <Input
                placeholder="Enter your last name"
                className="rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              />
            </Form.Item>

            <Form.Item
              label="Company Name"
              name="company_name"
              rules={[
                { required: true, message: "Please enter your company name!" },
              ]}
            >
              <Input
                placeholder="Enter your company name"
                className="rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
              />
            </Form.Item>
            <Form.Item label="Phone Number" name="phone_number">
              <Input
                placeholder="Enter your phone number"
                className="rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500"
                onKeyDown={handlePhoneNumberKeyDown}
                onChange={(e) => {
                  const formatted = formatPhoneNumber(e.target.value);
                  form.setFieldsValue({ phone_number: formatted });
                }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg py-3"
              >
                <UserAddOutlined /> Register
              </Button>
            </Form.Item>
          </Form>

          <div className="text-left mt-4 text-gray-600">
            <Link to="/login" className="text-blue-600 hover:underline">
              Already have an account? Log in
            </Link>
          </div>
        </div>
      </div>

      {/* Email Confirmation Modal */}
      <Modal
        title="Thanks for signing up!"
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        centered={true}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={() => setIsModalVisible(false)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            OK
          </Button>,
        ]}
      >
        <p className="text-base">
          Due to high demand, there is currently a waitlist.
        </p>
        <p className="text-base mt-4">
          You'll receive an email within a few minutes with more details on the
          current wait time and answers to frequently asked questions.
        </p>
        <p className="text-base mt-4">
          <span className="font-medium">
            Sometimes it lands in spam, so be sure to check there too!
          </span>
        </p>
      </Modal>
    </div>
  );
};

export default Register;
